import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import type { SICReportAccount, SICAccountResultStatus } from '@/types/sic-report.types';

interface Props {
    account: SICReportAccount;
    onClose: () => void;
}

const STATUS_VARIANT: Record<SICAccountResultStatus, { label: string; color: string }> = {
    CONFIRMED: { label: 'Confirmado', color: 'bg-emerald-500/20 text-emerald-400' },
    REJECTED: { label: 'Rechazado', color: 'bg-amber-500/20 text-amber-400' },
    ERROR: { label: 'Error', color: 'bg-red-500/20 text-red-400' },
};

function LabelValue({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
    return (
        <div className="flex justify-between gap-4 py-1">
            <span className="text-sm text-slate-400">{label}</span>
            <span className={`text-sm text-slate-200 ${mono ? 'font-mono' : ''}`}>
                {value ?? <span className="text-slate-500 italic">—</span>}
            </span>
        </div>
    );
}

function SectionHeader({ title }: { title: string }) {
    return (
        <div className="mb-2 mt-4 border-b border-slate-700 pb-1">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h4>
        </div>
    );
}

export function SICAccountDetailModal({ account, onClose }: Props) {
    const [copied, setCopied] = useState(false);
    const variant = STATUS_VARIANT[account.status];

    const handleCopyRaw = () => {
        navigator.clipboard.writeText(JSON.stringify(account.provider_response_raw ?? account, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Modal isOpen onClose={onClose} size="lg" title={`Cuenta ${account.credit_id}`}>
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <Badge className={variant.color}>{variant.label}</Badge>
                    <span className="text-xs text-slate-400">{account.report_type}</span>
                </div>

                <SectionHeader title="Identificadores" />
                <LabelValue label="Credit ID" value={account.credit_id} mono />
                <LabelValue label="Directory ID" value={account.directory_id} mono />
                <LabelValue label="Idempotency Key" value={account.idempotency_key} mono />

                <SectionHeader title="Resultado" />
                <LabelValue label="Estado" value={variant.label} />
                <LabelValue label="Folio CDC" value={account.cdc_folio} mono />
                <LabelValue label="Enviado" value={new Date(account.sent_at).toLocaleString('es-MX')} />
                <LabelValue label="Hash payload" value={account.payload_hash.slice(0, 16) + '…'} mono />

                {account.error_code && (
                    <>
                        <SectionHeader title="Error" />
                        <LabelValue label="Código" value={account.error_code} />
                        <LabelValue label="Mensaje" value={account.error_message} />
                        {account.cdc_errors?.errores && (
                            <div className="mt-2 space-y-1">
                                {account.cdc_errors.errores.map((e, idx) => (
                                    <div key={idx} className="rounded border border-red-500/20 bg-red-500/5 px-2 py-1 text-xs">
                                        <span className="font-mono text-red-400">{e.codigo}</span>
                                        <span className="ml-2 text-slate-300">{e.mensaje}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {(account.outstanding_balance != null || account.payment_history) && (
                    <>
                        <SectionHeader title="Reporte enviado a CDC" />
                        <LabelValue label="Tipo" value={account.report_type} />
                        <LabelValue label="Saldo" value={account.outstanding_balance != null ? `$${account.outstanding_balance.toFixed(2)}` : null} mono />
                        <LabelValue label="Saldo vencido" value={account.overdue_balance != null ? `$${account.overdue_balance.toFixed(2)}` : null} mono />
                        <LabelValue label="Cupones vencidos" value={account.overdue_payments_count ?? null} />
                        <LabelValue label="Días de mora" value={account.overdue_days ?? null} />
                        <LabelValue label="Total pagos reportados" value={account.total_reported_payments ?? null} />
                        <LabelValue label="Último pago" value={account.last_payment_at ?? null} />
                        <LabelValue label="Monto último pago" value={account.last_payment_amount != null ? `$${account.last_payment_amount.toFixed(2)}` : null} mono />
                        {account.payment_history && account.payment_history.length > 0 && (
                            <div className="mt-2">
                                <div className="text-xs text-slate-400 mb-1">Histórico de pagos (más reciente → más antiguo)</div>
                                <div className="flex flex-wrap gap-1">
                                    {account.payment_history.map((p, idx) => (
                                        <span
                                            key={idx}
                                            className={`rounded px-2 py-0.5 text-xs font-mono ${
                                                p === 'ON_TIME' ? 'bg-emerald-500/20 text-emerald-400' :
                                                p === 'WRITTEN_OFF' ? 'bg-red-500/30 text-red-300' :
                                                p === 'NO_DATA' ? 'bg-slate-500/20 text-slate-400' :
                                                'bg-amber-500/20 text-amber-400'
                                            }`}
                                        >
                                            {p}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <SectionHeader title="Respuesta cruda CDC" />
                <pre className="max-h-64 overflow-auto rounded bg-slate-900 p-3 text-xs text-slate-300">
                    {JSON.stringify(account.provider_response_raw ?? '(no response captured)', null, 2)}
                </pre>

                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={handleCopyRaw} className="inline-flex items-center gap-1">
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? 'Copiado' : 'Copiar JSON'}
                    </Button>
                    <Button onClick={onClose}>Cerrar</Button>
                </div>
            </div>
        </Modal>
    );
}
